import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import { all_routes } from "../router/all_routes";
import { loginUser } from "../../api/authService";
import { toast } from "react-toastify";
import { Badge } from "react-bootstrap";
import { login } from "../../core/data/redux/authSlice";
import { useDispatch } from "react-redux";
import { startAutoLockWatcher } from "../../api/autolock";
import { jwtDecode, JwtPayload } from "jwt-decode";

type CustomJwtPayload = JwtPayload & {
  role?: string;
  id?: string;
  client_id?: string;
  username?: string;
};

const handlePostLogin = (
  data: any,
  email: string,
  dispatch: any,
  navigate: any
) => {
  const fullToken = `${data.token_type ?? "Bearer"} ${data.access_token}`;
  localStorage.setItem("authToken", fullToken);

  const decoded = jwtDecode<CustomJwtPayload>(data.access_token);

  if (decoded.exp) {
    localStorage.setItem("token_expiry", String(decoded.exp * 1000));
  }
  let expiryTimeMs: number | undefined;

if (typeof decoded.exp === "number") {
  expiryTimeMs = decoded.exp * 1000;

  localStorage.setItem("token_expiry", expiryTimeMs.toString());

  // 🧪 Debug log (login time)
  const now = Date.now();
  const diffMs = expiryTimeMs - now;

  const minutes = Math.floor(diffMs / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);

  console.log(
    `[LOGIN] Token expires in ${minutes}m ${seconds}s`,
    {
      exp: decoded.exp,
      expiryTimeMs,
      now,
    }
  );
}

  const role = decoded.role ?? "user";
  const clientId = decoded.client_id ?? "";
  const userId = decoded.id ?? "";
  const username = decoded.username ?? "";

  // 🔑 CONSISTENT KEYS
  localStorage.setItem("userRole", role);
  localStorage.setItem("clientId", clientId);
  localStorage.setItem("user_id", userId);
  localStorage.setItem("username", username);
  localStorage.setItem("email", email);

  dispatch(
    login({
      token: fullToken,
      role,
      client_id: clientId,
      user_id: userId,
      username,
      e_mail: email,
    })
  );

  localStorage.removeItem("isLocked");

  // 🚦 Redirect
  if (role === "superadmin") navigate(all_routes.superadminDashboard);
  else if (role === "admin") navigate(all_routes.dealsDashboard);
  else navigate(all_routes.leadsDashboard);
};

const LockScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    specialChar: false,
  });
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  const username = localStorage.getItem("username") || "User";
  const role = localStorage.getItem("userRole") || "user";
  const email = localStorage.getItem("email") || "";



  const redirectByRole = useCallback(() => {
    if (role === "superadmin") navigate(all_routes.superadminDashboard);
    else if (role === "admin") navigate(all_routes.dealsDashboard);
    else if (role === "client") navigate(all_routes.manageusers);
    else navigate(all_routes.leadsDashboard);
  }, [navigate, role]);

    /* 🔁 Auth Guard */
// useEffect(() => {
//   const token = localStorage.getItem("authToken");
//   const isLocked = localStorage.getItem("isLocked");

//   if (!token) {
//     navigate(all_routes.login);
//     return;
//   }

//   if (!isLocked) redirectByRole();
// }, [navigate, redirectByRole]);
/* 🔁 Auth Guard */
useEffect(() => {
  const token = localStorage.getItem("authToken");
  const isLocked = localStorage.getItem("isLocked");
  const expiry = Number(localStorage.getItem("token_expiry"));

  // ⏰ Token expired → force login
  if (expiry && Date.now() > expiry) {
    localStorage.clear();
    toast.info("Session expired. Please login again.");
    navigate(all_routes.login);
    return;
  }

  // 🔐 No token → login
  if (!token) {
    navigate(all_routes.login);
    return;
  }

  // 🔓 Not locked → continue
  if (!isLocked) {
    redirectByRole();
  }
}, [navigate, redirectByRole]);
// useEffect(() => {
//   window.history.pushState(null, "", window.location.href);

//   const onPopState = () => {
//     window.history.pushState(null, "", window.location.href);
//   };

//   window.addEventListener("popstate", onPopState);

//   return () => {
//     window.removeEventListener("popstate", onPopState);
//   };
// }, []);

useEffect(() => {
  const onPopState = () => {
    // Clear session-related flags
    localStorage.removeItem("isLocked");

    // Optional: full logout
    localStorage.clear();

    navigate(all_routes.login, { replace: true });
  };

  window.addEventListener("popstate", onPopState);

  return () => {
    window.removeEventListener("popstate", onPopState);
  };
}, [navigate]);

  /* 🔑 Password Validation */
  const validatePassword = (value: string) => {
    const validations = {
      length: value.length >= 8,
      uppercase: /[A-Z]/.test(value),
      lowercase: /[a-z]/.test(value),
      digit: /[0-9]/.test(value),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(value),
    };

    setPasswordValidations(validations);

    if (!value.trim()) {
      return "Password is required";
    }

    if (value.length > 128) {
      return "Password must not exceed 128 characters";
    }

    if (/\s{2,}/.test(value)) {
      return "Password cannot contain consecutive spaces";
    }

    if (/(.)\1{5,}/.test(value)) {
      return "Password contains too many repeated characters";
    }

    // Dynamic live feedback
    const missing: string[] = [];
    if (!validations.length) missing.push("at least 8 characters");
    if (!validations.uppercase) missing.push("one uppercase letter");
    if (!validations.lowercase) missing.push("one lowercase letter");
    if (!validations.digit) missing.push("one number");
    if (!validations.specialChar) missing.push("one special character");

    if (missing.length > 0) {
      return `Password must include: ${missing.join(", ")}`;
    }

    return "";
  };
const handleUnlock = async () => {
  setTouched(true);
  const error = validatePassword(password);
  setPasswordError(error);

  if (error) {
    toast.error(error);
    return;
  }

  try {
    setLoading(true);

    const data = await loginUser(email, password, false);

    if (!data?.access_token) {
      toast.error("Invalid password");
      return;
    }

    handlePostLogin(data, email, dispatch, navigate);
    startAutoLockWatcher(navigate);

    toast.success("Session unlocked");
  } catch {
    toast.error("Incorrect password");
  } finally {
    setLoading(false);
  }
};

  /* ⌨ Enter key support */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !loading) handleUnlock();
  };

  /* Shine animation keyframes */
  useEffect(() => {
    const styleId = "animated-badge-keyframes";
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.innerText = `
        @keyframes shine {
          0% { transform: skewX(-20deg) translateX(-150%); }
          50% { transform: skewX(-20deg) translateX(150%); }
          100% { transform: skewX(-20deg) translateX(150%); }
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  return (
    <div className="login-page-container">
      {/* LEFT SIDE – CARD */}
      <div className="login-right-card">
        <div className="login-wrapper" role="main">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleUnlock();
            }}
            aria-live="polite"
          >
            {/* LOGO */}
            <div className="text-center mb-4">
              <div className="logo-wrapper">
                <ImageWithBasePath
                  src="assets/img/logo.png"
                  className="img-fluid"
                  alt="IATT CRMS Logo"
                />
              </div>
            </div>

            {/* USER INFO */}
            <div className="text-center mb-4">
              <h3 className="semibold">{username.toUpperCase()}</h3>
              <div className="mt-2">
                <Badge bg="secondary" pill>
                  {role.toUpperCase()}
                </Badge>
              </div>
              <p className="text-muted mt-3">Your session is locked</p>
            </div>

            {/* PASSWORD INPUT */}
            <div className="mb-3">
              <label htmlFor="password" className="form-label">
                Password <span className="text-danger">*</span>
              </label>

              <div className="input-with-icon">
                <i className="bi bi-lock"></i>
                <input
                  id="password"
                  type={isPasswordVisible ? "text" : "password"}
                  className={`form-control ${
                    passwordError && touched ? "is-invalid" : ""
                  }`}
                  value={password}
                  autoFocus
                  placeholder="Enter your password"
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (touched) {
                      setPasswordError(validatePassword(e.target.value));
                    }
                  }}
                  onBlur={() => {
                    setTouched(true);
                    setPasswordError(validatePassword(password));
                  }}
                  onKeyDown={handleKeyDown}
                />
                <span
                  className={`position-absolute end-0 top-50 translate-middle-y me-3 cursor-pointer ${
                    isPasswordVisible ? "ti ti-eye" : "ti ti-eye-off"
                  }`}
                  style={{ fontSize: "18px", opacity: 0.7, zIndex: 10 }}
                  onClick={() => setPasswordVisible((prev) => !prev)}
                />
              </div>

              {/* Live Validation Message */}
              {touched && passwordError && (
                <small className="text-danger d-block mt-1">
                  {passwordError}
                </small>
              )}
            </div>

            {/* UNLOCK BUTTON */}
            <button
              type="submit"
              className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
              disabled={loading}
              aria-busy={loading}
              style={{
                position: "relative",
                overflow: "hidden",
              }}
            >
              {loading && (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                  style={{ position: "relative", zIndex: 2 }}
                />
              )}
              <span style={{ position: "relative", zIndex: 2 }}>
                {loading ? "Unlocking..." : "Unlock Session"}
              </span>
              <span
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)",
                  transform: "skewX(-20deg) translateX(-150%)",
                  animation: "shine 3s infinite",
                }}
              />
            </button>

            {/* SIGN IN ANOTHER ACCOUNT */}
            <div className="text-center mt-4">
              <h6>
                <Link to={all_routes.login} className="text-primary fw-medium">
                  Sign in with another account
                </Link>
              </h6>
            </div>

            {/* FOOTER */}
            <div className="text-center mt-2">
              <p className="footer-text">© 2025 - IATT | CRMS</p>
            </div>
          </form>
        </div>
      </div>

      {/* RIGHT SIDE – STATIC IMAGE */}
      <div className="login-left-image">
        <ImageWithBasePath
          src="assets/img/authentication/reset-img.png"
          className="img-fluid auth-static-img"
          alt="Session locked illustration"
        />
      </div>

      {/* STYLES */}
      <style>{`
        .login-page-container {
          display: flex;
          height: 100vh;
          width: 100%;
          overflow: hidden;
        }

        .login-right-card {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
          background: linear-gradient(135deg, #ffeaea 0%, #fff5f5 50%, #ffdede 100%);
        }

        .login-wrapper {
          width: 100%;
          max-width: 420px;
          background: #ffffff;
          border-radius: 18px;
          padding: 32px;
          box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);
          animation: fadeInUp 0.8s ease forwards;
        }

        .logo-wrapper img {
          width: 120px;
        }

        .login-left-image {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f8f9fa;
          animation: fadeInDelayed 1s ease forwards;
        }

        .auth-static-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 40px;
        }

        .footer-text {
          color: #aaa;
          font-size: 13px;
        }

        .form-control:focus,
        .btn:focus {
          outline: none;
          border-color: #e41f07;
          box-shadow: 0 0 0 3px rgba(228, 31, 7, 0.25);
        }

        @media (max-width: 768px) {
          .login-left-image {
            display: none;
          }
          .login-right-card {
            padding: 20px;
          }
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes fadeInDelayed {
          from { opacity: 0; }
          to { opacity: 1; }
        }
.input-with-icon {
  position: relative;
}

.input-with-icon .form-control {
  padding-left: 44px !important;   /* Space for lock icon */
  padding-right: 48px !important; /* Space for eye icon */
  height: 48px;                   /* Slightly taller for better alignment */
  border-radius: 8px;
  font-size: 15px;
}

/* Lock icon (left) */
.input-with-icon i.bi-lock {
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  font-size: 18px;
  color: #6c757d;
  opacity: 0.8;
  pointer-events: none;
  z-index: 5;
}

/* Eye toggle icon (right) */
.input-with-icon .ti-eye,
.input-with-icon .ti-eye-off {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  font-size: 19px;
  color: #6c757d;
  opacity: 0.7;
  z-index: 10;
  cursor: pointer;
  pointer-events: auto;
  line-height: 1;
}
/* Remove bootstrap invalid icon so custom eye toggle works */
.input-with-icon .form-control.is-invalid {
  background-image: none !important;
}

/* Focus state */
.input-with-icon .form-control:focus {
  border-color: #e41f07;
  box-shadow: 0 0 0 3px rgba(228, 31, 7, 0.18);
}
        .cursor-pointer {
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default LockScreen;
